"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { useLocale, useT } from "@/lib/use-i18n";
import { LOCALE_META, localePath, SUPPORTED_LOCALES, type Locale } from "@/lib/i18n/locales";
import { cn } from "@/lib/utils";
import { computePopoverPosition, type PopoverPosition } from "@/lib/popover-position";
function setLocaleCookie(locale: string) {
  // 2026-09-21 FASE 2 P2 cookie Secure condicional (research 2026 MDN
  // Set-Cookie Secure + Next.js locale-cookie patterns: Secure en
  // http://localhost impide persistir la preferencia en dev; solo en https/prod).
  // Paridad con src/proxy.ts que fija secure solo en production.
  const secure = typeof window !== "undefined" && (window.location.protocol === "https:" || process.env.NODE_ENV === "production") ? "; Secure" : "";
  document.cookie = `locale=${locale}; path=/; max-age=31536000; SameSite=Lax${secure}`;
}
function navigateToLocale(locale: Locale, pathname: string) {
  setLocaleCookie(locale);
  const next = localePath(locale, pathname);
  // Full navigation so proxy/rewrites and SSR locale headers stay in sync.
  if (next === pathname) {
    window.location.reload();
    return;
  }
  window.location.assign(next);
}
function LocaleFlag(t0) {
  const $ = _c(7);
  const {
    locale,
    className,
    testId,
    lazy: t1
  } = t0;
  const lazy = t1 === undefined ? false : t1;
  const meta = LOCALE_META[locale];
  const t2 = meta.flagSrc;
  const t3 = lazy ? "lazy" : undefined;
  let t4;
  if ($[0] !== className) {
    t4 = cn("lang-selector-flag", className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = testId ?? `lang-flag-${locale}`;
  let t6;
  if ($[2] !== meta.flagSrc || $[3] !== t3 || $[4] !== t4 || $[5] !== t5) {
    t6 = <img src={t2} alt="" width={20} height={14} decoding="async" loading={t3} className={t4} data-testid={t5} aria-hidden="true" />;
    $[2] = meta.flagSrc;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}

/**
 * Language switcher — menu is portaled + position:fixed under the trigger.
 * Absolute dropdowns break after scroll inside sticky liquid-glass header
 * (backdrop-filter creates a containing block; menu opens off-screen).
 */
export function LanguageSelector() {
  const $ = _c(51);
  const currentLocale = useLocale();
  const pathname = usePathname() || "/";
  const t = useT();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      top: 0,
      left: 0,
      right: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [coords, setCoords] = useState(t0);
  const [mounted] = useState(_temp);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const optionRefs = useRef(t1);
  const currentMeta = LOCALE_META[currentLocale];
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const btn = buttonRef.current;
      if (!btn) {
        return;
      }
      const r = btn.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || 200;
      const menuHeight = menuRef.current?.offsetHeight || 340;
      setCoords(computePopoverPosition(r, menuWidth, menuHeight, "end"));
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const updateCoords = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setOpen(false);
      setFocusIndex(-1);
      buttonRef.current?.focus();
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const close = t3;
  let t4;
  let t5;
  if ($[4] !== open) {
    t4 = () => {
      if (!open) {
        return;
      }
      updateCoords();
    };
    t5 = [open, updateCoords];
    $[4] = open;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useLayoutEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== open) {
    t6 = () => {
      if (!open) {
        return;
      }
      const onReposition = () => updateCoords();
      window.addEventListener("resize", onReposition);
      window.addEventListener("scroll", onReposition, {
        passive: true,
        capture: true
      });
      window.visualViewport?.addEventListener("resize", onReposition);
      window.visualViewport?.addEventListener("scroll", onReposition);
      return () => {
        window.removeEventListener("resize", onReposition);
        window.removeEventListener("scroll", onReposition, true);
        window.visualViewport?.removeEventListener("resize", onReposition);
        window.visualViewport?.removeEventListener("scroll", onReposition);
      };
    };
    t7 = [open, updateCoords];
    $[7] = open;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] !== open) {
    t8 = () => {
      if (!open) {
        return;
      }
      const handler = e => {
        const target = e.target as Node;
        if (ref.current?.contains(target)) {
          return;
        }
        if (menuRef.current?.contains(target)) {
          return;
        }
        const el = target as HTMLElement | null;
        if (el?.closest?.("[data-dropdown-content]")) {
          if (ref.current && el.closest("[data-dropdown-content]")?.contains(ref.current)) {
            return;
          }
        }
        close();
      };
      document.addEventListener("mousedown", handler);
      document.addEventListener("click", handler);
      return () => {
        document.removeEventListener("mousedown", handler);
        document.removeEventListener("click", handler);
      };
    };
    t9 = [open, close];
    $[10] = open;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] !== focusIndex || $[14] !== open) {
    t10 = () => {
      if (open && focusIndex >= 0) {
        optionRefs.current[focusIndex]?.focus();
      }
    };
    t11 = [open, focusIndex];
    $[13] = focusIndex;
    $[14] = open;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  if ($[17] !== pathname) {
    t12 = locale => {
      navigateToLocale(locale, pathname);
    };
    $[17] = pathname;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const selectLocale = t12;
  let t13;
  if ($[19] !== focusIndex || $[20] !== open || $[21] !== selectLocale) {
    t13 = e_0 => {
      if (!open) {
        if (e_0.key === "Enter" || e_0.key === " " || e_0.key === "ArrowDown") {
          e_0.preventDefault();
          updateCoords();
          setOpen(true);
          setFocusIndex(0);
        }
        return;
      }
      bb154: switch (e_0.key) {
        case "Escape":
          {
            e_0.preventDefault();
            close();
            break bb154;
          }
        case "ArrowDown":
          {
            e_0.preventDefault();
            setFocusIndex(_temp2);
            break bb154;
          }
        case "ArrowUp":
          {
            e_0.preventDefault();
            setFocusIndex(_temp3);
            break bb154;
          }
        case "Enter":
        case " ":
          {
            e_0.preventDefault();
            if (focusIndex >= 0) {
              selectLocale(SUPPORTED_LOCALES[focusIndex]);
            }
            break bb154;
          }
        case "Tab":
          {
            close();
          }
      }
    };
    $[19] = focusIndex;
    $[20] = open;
    $[21] = selectLocale;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleKeyDown = t13;
  let t14;
  if ($[23] !== coords || $[24] !== currentLocale || $[25] !== handleKeyDown || $[26] !== mounted || $[27] !== open || $[28] !== selectLocale || $[29] !== t) {
    t14 = open && mounted ? createPortal(<div ref={menuRef} id="language-listbox" role="listbox" aria-label={t("languageSelectorLabel")} onKeyDown={handleKeyDown} data-testid="language-selector-menu" onMouseDown={_temp4} onClick={_temp5} onWheel={_temp6} onTouchMove={_temp7} style={{
      position: "fixed",
      top: coords.top,
      right: coords.right
    }} className="chrome-dropdown-menu z-[10000] w-[13.5rem] max-w-[calc(100vw-1rem)] max-h-[340px] max-h-[min(340px,70dvh)] overflow-y-auto overscroll-contain touch-pan-y scrollbar-none rounded-[var(--radius-card)] border border-[var(--border-strong)] bg-[var(--background-elevated)] p-1 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_16px_40px_rgba(0,0,0,0.55)]">{SUPPORTED_LOCALES.map((locale_0, index) => <button key={locale_0} ref={el_0 => {
        optionRefs.current[index] = el_0;
      }} type="button" role="option" aria-selected={locale_0 === currentLocale} onMouseDown={_temp8} onClick={e_2 => {
        e_2.stopPropagation();
        selectLocale(locale_0);
      }} className={cn("lang-selector-option", locale_0 === currentLocale && "lang-selector-option--active bg-[var(--accent-soft)]")}><LocaleFlag locale={locale_0} testId={`lang-flag-option-${locale_0}`} lazy={true} /><span className="truncate">{LOCALE_META[locale_0].nameNative}</span></button>)}</div>, document.body) : null;
    $[23] = coords;
    $[24] = currentLocale;
    $[25] = handleKeyDown;
    $[26] = mounted;
    $[27] = open;
    $[28] = selectLocale;
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const menu = t14;
  let t15;
  if ($[31] !== open) {
    t15 = () => {
      if (!open) {
        updateCoords();
      }
      setOpen(_temp9);
    };
    $[31] = open;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("languageSelectorLabel");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = `lang-flag-trigger-${currentLocale}`;
  let t18;
  if ($[35] !== currentLocale || $[36] !== t17) {
    t18 = <LocaleFlag locale={currentLocale} className="lang-selector-flag--trigger" testId={t17} />;
    $[35] = currentLocale;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== currentLocale) {
    t19 = <span className="lang-selector-code hidden sm:inline">{currentLocale}</span>;
    $[38] = currentLocale;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== currentMeta.nameNative || $[41] !== handleKeyDown || $[42] !== open || $[43] !== t15 || $[44] !== t16 || $[45] !== t18 || $[46] !== t19) {
    t20 = <button ref={buttonRef} type="button" onClick={t15} onKeyDown={handleKeyDown} className="lang-selector-trigger" aria-label={t16} aria-haspopup="listbox" aria-expanded={open} aria-controls="language-listbox" title={currentMeta.nameNative}>{t18}{t19}</button>;
    $[40] = currentMeta.nameNative;
    $[41] = handleKeyDown;
    $[42] = open;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== menu || $[49] !== t20) {
    t21 = <div ref={ref} className="lang-selector relative" data-testid="language-selector">{t20}{menu}</div>;
    $[48] = menu;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}
function _temp9(v) {
  return !v;
}
function _temp8(e_1) {
  return e_1.stopPropagation();
}
function _temp7(event_2) {
  return event_2.stopPropagation();
}
function _temp6(event_1) {
  return event_1.stopPropagation();
}
function _temp5(event_0) {
  return event_0.stopPropagation();
}
function _temp4(event) {
  return event.stopPropagation();
}
function _temp3(prev) {
  return (prev - 1 + SUPPORTED_LOCALES.length) % SUPPORTED_LOCALES.length;
}
function _temp2(prev_0) {
  return (prev_0 + 1) % SUPPORTED_LOCALES.length;
}
function _temp() {
  return typeof document !== "undefined";
}